import { set } from "date-fns";
import { useState, useEffect } from "react";

interface Coordinates {
  latitude: number;
  longitude: number;
  accuracy: number;
}

interface LocationHook {
  coordinates: Coordinates | null;
  error: string | null;
  loading: boolean;
}

const useGPSLocation = (requiredAccuracy: number | null = null): LocationHook => {
  const [coordinates, setCoordinates] = useState<Coordinates | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    let watchId: number;

    const handleSuccess = (position: GeolocationPosition) => {
      setCoordinates({
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
        accuracy: position.coords.accuracy,
      });
      setLoading(false);
    };

    const handleError = (error: GeolocationPositionError) => {
      let errorMessage: string;

      switch (error.code) {
        case error.PERMISSION_DENIED:
          errorMessage = "Please enable location services to continue.";
          break;
        case error.POSITION_UNAVAILABLE:
          errorMessage = "Location information is currently unavailable.";
          break;
        case error.TIMEOUT:
          errorMessage = "Location request timed out.";
          break;
        default:
          errorMessage = "An unknown error occurred while getting location.";
      }

      setError(errorMessage);
      setLoading(false);
    };

    if ("geolocation" in navigator) {
      const options = {
        enableHighAccuracy: false,
        timeout: 100000,
        maximumAge: 0,
      };

      watchId = navigator.geolocation.watchPosition(
        handleSuccess,
        handleError,
        options
      );
    } else {
      setError("Geolocation is not supported by your browser.");
      setLoading(false);
    }

    return () => {
      if (watchId) {
        navigator.geolocation.clearWatch(watchId);
      }
    };
  }, [requiredAccuracy]);

  return { coordinates, error, loading };
};

export default useGPSLocation;
